/**
 * UserTeams Component
 * Displays and manages a user's team memberships
 */

import React, { useState } from 'react';
import { Trash2, Plus } from 'lucide-react';
import { Card, Button, Badge, Select, LoadingSpinner } from '../common';
import {
  useUserTeams,
  useUpdateUserTeam,
  useRemoveUserTeam,
  checkAffectedTeamOKRs,
} from '../../hooks';
import { AddTeamModal } from './AddTeamModal';

export interface UserTeamsProps {
  userId: string;
  canEdit?: boolean;
}

export const UserTeams: React.FC<UserTeamsProps> = ({ userId, canEdit = true }) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [teamToRemove, setTeamToRemove] = useState<string | null>(null);

  const { data: teams = [], isLoading } = useUserTeams(userId);
  const updateMutation = useUpdateUserTeam();
  const removeMutation = useRemoveUserTeam();

  // Get role badge variant
  const getRoleBadgeVariant = (role: string | null): 'primary' | 'warning' | 'secondary' => {
    if (!role) return 'secondary';
    switch (role.toLowerCase()) {
      case 'manager':
        return 'primary';
      case 'lead':
        return 'warning';
      case 'member':
      default:
        return 'secondary';
    }
  };

  // Handle role change
  const handleRoleChange = async (teamId: string, newRole: string) => {
    try {
      await updateMutation.mutateAsync({
        userId,
        teamId,
        data: { role: newRole || null },
      });
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  // Handle remove team - checks for affected OKRs first
  const handleRemove = async (teamId: string, teamName: string) => {
    try {
      setTeamToRemove(teamId);

      const affectedOKRs = await checkAffectedTeamOKRs(userId, teamId);

      let confirmMessage: string;
      let shouldForce = false;

      if (affectedOKRs.count > 0) {
        const okrList = affectedOKRs.okrs.slice(0, 3).map(okr => `  • ${okr.title}`).join('\n');
        const moreText = affectedOKRs.count > 3 ? `\n  ... and ${affectedOKRs.count - 3} more` : '';

        confirmMessage =
          `WARNING: ${affectedOKRs.count} OKR(s) are associated with team "${teamName}":\n\n` +
          `${okrList}${moreText}\n\n` +
          `If you proceed, the team association will be cleared from these OKRs.\n\n` +
          `Do you want to continue?`;
        shouldForce = true;
      } else {
        confirmMessage = `Are you sure you want to remove this user from team "${teamName}"?\n\nThis action cannot be undone.`;
      }

      const confirmed = window.confirm(confirmMessage);

      if (confirmed) {
        await removeMutation.mutateAsync({ userId, teamId, force: shouldForce });
      }
    } catch (error) {
      // Error handling is done in the hook
    } finally {
      setTeamToRemove(null);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="flex items-center justify-center py-8">
          <LoadingSpinner size="md" />
        </div>
      </Card>
    );
  }

  if (teams.length === 0) {
    return (
      <>
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-stone-900">Teams</h3>
            {canEdit && (
              <Button
                variant="primary"
                size="sm"
                onClick={() => setIsAddModalOpen(true)}
                icon={Plus}
              >
                Add Team
              </Button>
            )}
          </div>
          <div className="text-center py-8">
            <p className="text-stone-500 mb-4">This user is not assigned to any teams.</p>
            {canEdit && (
              <Button variant="outline" onClick={() => setIsAddModalOpen(true)} icon={Plus}>
                Add to Team
              </Button>
            )}
          </div>
        </Card>
        <AddTeamModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          userId={userId}
          existingTeamIds={[]}
        />
      </>
    );
  }

  return (
    <>
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-stone-900">Teams</h3>
          {canEdit && (
            <Button
              variant="primary"
              size="sm"
              onClick={() => setIsAddModalOpen(true)}
              icon={Plus}
            >
              Add Team
            </Button>
          )}
        </div>

        <div className="space-y-3">
          {teams.map((team) => (
            <div
              key={team.id}
              className="border border-stone-200 bg-white rounded-lg p-4 hover:border-stone-300 transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-stone-900 mb-1">{team.teamName}</h4>
                  {team.teamDescription && (
                    <p className="text-xs text-stone-400 mb-2">{team.teamDescription}</p>
                  )}

                  <div className="flex flex-wrap items-center gap-2">
                    {canEdit ? (
                      <>
                        <label className="text-sm text-stone-600">Role:</label>
                        <Select
                          value={team.role || ''}
                          onChange={(e) => handleRoleChange(team.teamId, e.target.value)}
                          options={[
                            { value: '', label: 'No Role', disabled: false },
                            { value: 'member', label: 'Member', disabled: false },
                            { value: 'lead', label: 'Lead', disabled: false },
                            { value: 'manager', label: 'Manager', disabled: false },
                          ]}
                          className="w-40 text-sm"
                          disabled={updateMutation.isPending}
                        />
                      </>
                    ) : team.role ? (
                      <>
                        <span className="text-sm text-stone-600">Role:</span>
                        <Badge variant={getRoleBadgeVariant(team.role)} size="sm">
                          {team.role}
                        </Badge>
                      </>
                    ) : null}
                  </div>
                </div>

                {canEdit && (
                  <div className="flex items-center gap-2 ml-4">
                    <Button
                      variant="danger"
                      size="sm"
                      onClick={() => handleRemove(team.teamId, team.teamName)}
                      disabled={
                        removeMutation.isPending && teamToRemove === team.teamId
                      }
                      className="text-xs"
                    >
                      {removeMutation.isPending && teamToRemove === team.teamId ? (
                        <LoadingSpinner size="sm" className="h-3 w-3" />
                      ) : (
                        <Trash2 className="h-3 w-3" />
                      )}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 pt-4 border-t border-stone-200">
          <p className="text-xs text-stone-500">
            {teams.length === 1
              ? 'This user belongs to 1 team'
              : `This user belongs to ${teams.length} teams`}
          </p>
        </div>
      </Card>

      <AddTeamModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        userId={userId}
        existingTeamIds={teams.map((t) => t.teamId)}
      />
    </>
  );
};
